/**
 * SendTextChatMessageUseCase
 *
 * Encrypt-then-write hop for a single text chat message. Round-one scope only
 * supports `ChatMessageType.text`; non-text payloads are a programmer error
 * (the chat composer never produces them in round one) and short-circuit with
 * a logged early return.
 *
 * Flutter source: lib/data/repository/chat_repository.dart:14-25 —
 * `ChatRepository.saveChatMessage`. When `chatMessage.content != null && content!.isNotEmpty`
 * Flutter encrypts via `E2EE.encrypt` and re-wraps the message before delegating
 * to `ChatDataSource.saveChatMessage`. The web port mirrors that exactly: the
 * already-encrypted ciphertext is passed to `ChatService.saveChatMessage`
 * alongside the original entity; the service remaps to DTO and applies the
 * override into both the message doc's `content` and the metadata doc's
 * `last_content`. The original entity is NEVER mutated — the caller (`useChat`)
 * retains the plaintext copy for optimistic rendering, matching
 * `chat_screen.dart:3699-3712` where the unencrypted message is also what gets
 * inserted at position 0 in the live list.
 *
 * Pure-domain contract:
 *   - The use case calls `EncryptTextUseCase` internally — no callback args.
 *     The caller is responsible for having previously invoked
 *     `InitializeTextEncryptionUseCase.execute(peer.publicJwk)` so the
 *     singleton's derived key is bound to the intended peer.
 *   - The use case does NOT touch `firestoreService` directly and does NOT
 *     dispatch to Redux — both are presentation concerns.
 */

import { ChatService } from '@/domain/services/chat/chatService';
import { EncryptTextUseCase, encryptTextUseCase } from '@/domain/usecases/textEncryption/encryptTextUseCase';
import type { ChatMessageEntity } from '@/domain/entities/chat/chatMessageEntity';
import type { ChatUsersInfoEntity } from '@/domain/entities/info/chatUsersInfoEntity';
import { ChatMessageType } from '@/domain/enums/chatMessageType';
import { appLogger } from '@/domain/services/analytics/appLogger';

export interface SendTextChatMessageArgs {
  chatMessage: ChatMessageEntity;
  chatUsersInfo: ChatUsersInfoEntity;
}

export class SendTextChatMessageUseCase {
  private chatService: ChatService;
  private encryptTextUseCase: EncryptTextUseCase;

  constructor(
    chatService: ChatService = ChatService.getInstance(),
    encryptUseCase: EncryptTextUseCase = encryptTextUseCase,
  ) {
    this.chatService = chatService;
    this.encryptTextUseCase = encryptUseCase;
  }

  /**
   * Encrypt `chatMessage.content` when set and non-empty, then persist the
   * message via `ChatService.saveChatMessage`. Returns the service's
   * success boolean unchanged.
   *
   * @returns `true` on success, `false` on Firestore-write failure, `false`
   *   when the message type is not `ChatMessageType.text` (programmer-error
   *   early return — see class JSDoc).
   */
  async execute(args: SendTextChatMessageArgs): Promise<boolean> {
    const { chatMessage, chatUsersInfo } = args;

    // Round-one scope: text only. A non-text payload reaching this use case is
    // a programmer error from the presentation layer (every non-text send path
    // currently stubs to `InfoSheet.showNotImplementedYet`). Log and bail
    // rather than throwing — the chat composer is unforgiving and a thrown
    // exception would surface as a noisy uncaught rejection while a returned
    // `false` is treated as a normal send failure by `useChat` (it'll surface
    // the send-error banner). The plan (Task 10) explicitly requires
    // `appLogger.error`, NOT a throw.
    if (chatMessage.type !== ChatMessageType.text) {
      appLogger.error(
        'Non-text chat message rejected (round-one scope is text-only).',
        'SendTextChatMessageUseCase.execute',
        {
          context: {
            documentId: chatMessage.documentId,
            type: chatMessage.type,
          },
        },
      );
      return false;
    }

    // Mirror chat_repository.dart:17-23 — encrypt iff content is non-null AND
    // non-empty. Dart's `content != null && content!.isNotEmpty` translates to
    // `content !== undefined && content !== ''` on web. Empty / undefined
    // content passes through with a `null` ciphertext (the service maps that
    // to `last_content: null` on the metadata doc, matching Flutter).
    const plaintext = chatMessage.content;
    const encryptedContent =
      plaintext !== undefined && plaintext !== ''
        ? await this.encryptTextUseCase.execute(plaintext)
        : null;

    // The entity is handed to the service untouched; the service remaps to
    // DTO internally and overrides `content` with the ciphertext before
    // serialization (see `ChatService.saveChatMessage` JSDoc +
    // `chat_repository.dart:19`). Passing the entity keeps the service's
    // input surface consistent with `chatUsersInfo: ChatUsersInfoEntity`.
    return this.chatService.saveChatMessage(chatMessage, chatUsersInfo, encryptedContent);
  }
}
